import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";

import { AppModel } from "../../../app/app-model";
import { useCountries } from "../../../catalogs/countries";
import { AddButton } from "../../../components/ui-kit/add-button";
import {
  Dialog as BaseDialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui-kit/base/dialog";
import { Button } from "../../../components/ui-kit/button";
import { Checkbox } from "../../../components/ui-kit/fields/checkbox";
import { CellTooltipList } from "../../../components/ui-kit/tables/cells/cell-tooltip-list";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { SelectionMode } from "../../../models/data-model";
import {
  getCountryOptions,
  getRegionOptions,
  regionDisabled,
  regionPredicate,
} from "../../../utils/filter-presets";
import { AddPhysician } from "../physicians/add-physician";
import { AddPhysicianForm } from "../physicians/add-physician-form";
import { Physicians, PhysiciansItem } from "../physicians/physicians";

/** Geography restriction of the physician list, unlockable via toggles. */
export interface PhysiciansGeoLock {
  country: string;
  region?: number;
}

export const PhysiciansModal = observer(function PhysiciansModal(props: {
  model: Physicians;
  mode: SelectionMode;
  preselected?: string[];
  filter?: Record<string, any[]>;
  title: React.ReactNode;
  onConfirm: (params: { selected: any[] }) => void | Promise<void>;
  onClose: () => void;
  allowCreate?: boolean;
  app?: AppModel;
  onAfterCreate?: () => Promise<void> | void;
  /**
   * When set, the list only shows physicians covering this country/region
   * (or physicians whose geography checks are bypassed), with toolbar
   * toggles to unlock the restriction. The model must be private to this
   * modal — the lock filters `model.data` in place.
   */
  geoLock?: PhysiciansGeoLock;
  showMonitoringUsers?: boolean;
}) {
  const { t } = useLingui();
  const countries = useCountries();
  const [view, setView] = useState<"list" | "create">("list");
  const [addModel, setAddModel] = useState<AddPhysician | null>(null);
  const [saving, setSaving] = useState(false);

  const { geoLock } = props;
  const [lockCountry, setLockCountry] = useState(true);
  const [lockRegion, setLockRegion] = useState(true);
  // Region 0 is the countries' "unknown" default — nothing to lock to.
  const lockableRegion =
    geoLock?.region != null && geoLock.region !== 0
      ? geoLock.region
      : undefined;

  // The lock filters model.data in place (the modal owns the model), keeping
  // the full list aside so unlocking restores it without a refetch.
  const allPhysiciansRef = useRef<PhysiciansItem[] | null>(null);
  const modelLoading = props.model.loading;
  useEffect(() => {
    if (!geoLock) return;
    if (modelLoading) {
      // A (re)fetch replaces model.data wholesale — drop the snapshot so the
      // post-load run re-captures the fresh list instead of restoring a
      // stale one.
      allPhysiciansRef.current = null;
      return;
    }
    allPhysiciansRef.current ??= props.model.data;
    const visible = allPhysiciansRef.current.filter((p) =>
      matchesGeoLock(p, geoLock, lockCountry, lockCountry && lockRegion),
    );
    props.model.data = visible;
    // Drop selections the lock just hid — Confirm acts on model.selected, so
    // a hidden row must not be silently included.
    const visibleIds = new Set(visible.map((p) => p.uuid));
    if (props.model.selected.some((uuid) => !visibleIds.has(uuid))) {
      props.model.selected = props.model.selected.filter((uuid) =>
        visibleIds.has(uuid),
      );
    }
  }, [geoLock, modelLoading, lockCountry, lockRegion, props.model]);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await props.onConfirm({ selected: [...props.model.selected] });
      props.onClose();
    } finally {
      setSaving(false);
    }
  };

  const enterCreateMode = () => {
    if (!props.app) return;
    const m = new AddPhysician(props.app.seerlinqApi);
    void m.initEmpty();
    setAddModel(m);
    setView("create");
  };

  const exitCreateMode = () => {
    setAddModel(null);
    setView("list");
  };

  const initialColumnFilters = props.filter?.country?.length
    ? [{ id: "country", value: props.filter.country }]
    : [];

  const renderListBody = () => (
    <>
      <div className="flex-1 min-h-0 w-full flex flex-col">
        <DataTable
          rowsPerPage={10}
          model={props.model}
          itemKeyField="uuid"
          isLoading={props.model.loading}
          fillHeight
          canSelect={props.mode}
          preselected={props.preselected}
          filterNoun={(count) =>
            t`${plural(count, { one: "physician", other: "physicians" })}`
          }
          filterActions={
            (props.allowCreate && props.app) || geoLock ? (
              <div className="flex items-center gap-4">
                {props.allowCreate && props.app && (
                  <AddButton onClick={enterCreateMode} disabled={saving}>
                    <Trans>New physician</Trans>
                  </AddButton>
                )}
                {geoLock && (
                  <>
                    <Checkbox
                      label={t`Lock to country: ${geoLock.country}`}
                      value={lockCountry}
                      onChange={setLockCountry}
                    />
                    {lockableRegion != null && (
                      <Checkbox
                        label={t`Lock to region: ${
                          countries
                            .getRegionsMap(geoLock.country)
                            .get(lockableRegion) ?? lockableRegion
                        }`}
                        value={lockCountry && lockRegion}
                        onChange={setLockRegion}
                        disabled={!lockCountry}
                      />
                    )}
                  </>
                )}
              </div>
            ) : undefined
          }
          initialColumnFilters={initialColumnFilters}
          columns={[
            {
              header: <Trans>Family name</Trans>,
              attr: "family_name",
              type: "string",
              sort: true,
              initSort: "asc",
              filter: {
                kind: "text",
                group: t`Identity`,
                placeholder: t`Search…`,
              },
              align: "left",
            },
            {
              header: <Trans>Name</Trans>,
              attr: "given_name",
              type: "string",
              filter: {
                kind: "text",
                label: t`Name`,
                group: t`Identity`,
                placeholder: t`Search…`,
              },
              align: "left",
            },
            {
              header: <Trans>Country</Trans>,
              attr: "country",
              type: "enum",
              values: countries.countriesEnum,
              filter: {
                kind: "multi",
                group: t`Location`,
                options: getCountryOptions,
              },
              align: "left",
            },
            {
              header: <Trans>Region</Trans>,
              attr: "region",
              align: "left",
              filter: {
                kind: "multi",
                group: t`Location`,
                options: (table) => getRegionOptions(table, countries),
                disabled: regionDisabled,
                disabledMessage: t`Select a country first`,
                predicate: regionPredicate,
              },
              cell: ({ item }) =>
                item.region != null
                  ? countries.getRegionSchema(item.country, item.region)
                      ?.region_name
                  : undefined,
            },
            {
              header: <Trans># of patients</Trans>,
              width: 30,
              cell: ({ item }) => item.numPatients,
            },
            props.showMonitoringUsers
              ? {
                  header: <Trans>Monitoring users</Trans>,
                  width: 200,
                  align: "left" as const,
                  cell: ({ item }: { item: PhysiciansItem }) => (
                    <CellTooltipList items={item.monitoringUserNames} />
                  ),
                }
              : null,
            {
              header: <Trans>Other clinics</Trans>,
              width: 200,
              cell: ({ item }) => item.clinicsStr,
            },
          ]}
        />
      </div>
      <DialogFooter>
        <Button onClick={props.onClose} disabled={saving}>
          <Trans>Cancel</Trans>
        </Button>
        <Button
          disabled={
            saving ||
            (props.model.selectionMode === "single" &&
              props.model.selected.length === 0)
          }
          pending={saving}
          onClick={() => void handleConfirm()}
        >
          <Trans>Confirm</Trans>
        </Button>
      </DialogFooter>
    </>
  );

  // The create form keeps its own scrolling since DialogContent no longer
  // scrolls (only the table rows scroll in list view).
  const renderCreateBody = () => {
    if (!addModel || !props.app) return null;
    return (
      <div className="flex-1 min-h-0 overflow-y-auto">
        <AddPhysicianForm
          app={props.app}
          model={addModel}
          compact
          onCancel={exitCreateMode}
          onSuccess={async (uuid) => {
            await props.onAfterCreate?.();
            if (props.model.selectionMode === "single") {
              props.model.selected = [uuid];
            } else if (!props.model.selected.includes(uuid)) {
              props.model.selected.push(uuid);
            }
            await handleConfirm();
          }}
        />
      </div>
    );
  };

  return (
    <BaseDialog
      open
      onOpenChange={(open) => {
        if (!open && !saving) props.onClose();
      }}
    >
      <DialogContent className="max-h-[80vh] sm:max-w-[calc(100vw-4rem)]">
        <DialogHeader>
          <DialogTitle>
            {view === "list" ? props.title : <Trans>Add new physician</Trans>}
          </DialogTitle>
        </DialogHeader>

        {view === "list" ? renderListBody() : renderCreateBody()}
      </DialogContent>
    </BaseDialog>
  );
});

function matchesGeoLock(
  physician: PhysiciansItem,
  geoLock: PhysiciansGeoLock,
  lockCountry: boolean,
  lockRegion: boolean,
): boolean {
  const [bypassCountry = false, bypassRegion = false] =
    physician.bypass_geography_checks ?? [];
  if (lockCountry && !bypassCountry && physician.country !== geoLock.country) {
    return false;
  }
  // A country bypass also skips the region check — a physician who may be
  // assigned across countries can't be pinned to a region within one.
  if (
    lockRegion &&
    geoLock.region != null &&
    geoLock.region !== 0 &&
    !bypassCountry &&
    !bypassRegion &&
    !(
      physician.country === geoLock.country &&
      physician.region === geoLock.region
    )
  ) {
    return false;
  }
  return true;
}
